import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseDestroy } from 'nera-common/angular';
import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { isSystemOrg } from 'app/core/constants/system-org.constants';
import { hasAnyRole, PLATFORM_STAFF_ROLES, ROLES } from 'app/shared/constants';
import { AbsoluteMediaUrlPipe } from 'app/shared/pipes/absolute-media-url.pipe';
import { environment } from 'environments/environment';
import { IUser } from '../../../core/models/auth.model';
import { AuthService } from '../../../core/services/auth.service';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    // Material ships `.mat-mdc-icon-button img { height: auto !important }`
    // (specificity 0,1,1) which beats Tailwind h-*/h-full !important (0,1,0)
    // and lets tall avatars overflow the 28px topbar slot. Win on specificity.
    styles: [
        'user .mat-mdc-icon-button img { width: 28px !important; height: 28px !important; object-fit: cover; }',
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        NgClass,
        TranslocoModule,
        RouterLink,
        AbsoluteMediaUrlPipe,
    ],
})
export class UserComponent extends BaseDestroy implements OnInit {
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    private readonly _router = inject(Router);
    private readonly _authService = inject(AuthService);

    static ngAcceptInputType_showAvatar: BooleanInput;

    @Input() showAvatar: boolean = true;
    user: IUser;
    canRequestStudio: boolean = false;
    canAccessAdminPortal: boolean = false;
    readonly saPortalUrl: string = environment.saPortalUrl;
    /** IAM portal owns profile / users / RBAC / sessions. */
    readonly iamPortalUrl: string =
        environment.iamPortalUrl.replace(/\/$/, '') + '/me';

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this._authService.user$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((user: IUser) => {
                this.user = user;
                this.canRequestStudio =
                    hasAnyRole(user?.roles ?? '', [ROLES.PHOTOGRAPHER]) &&
                    isSystemOrg(user?.organizationId || user?.org_id);
                // WS-B (PFL-2564): PLATFORM_ADMIN is new platform staff, same
                // "go to the admin portal" link as SUPER_ADMIN.
                this.canAccessAdminPortal = hasAnyRole(user?.roles ?? '', [
                    ...PLATFORM_STAFF_ROLES,
                ]);
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */

    signOut(): void {
        this._router.navigate(['/sign-out']).catch(() => {
            // Navigation to sign-out failed
        });
    }
}
